import { useEffect, useState } from "react";
import { FileText, Download } from "lucide-react";
import { api, rupee, API } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Pagination, usePageSlice } from "@/components/Pagination";
import { toast } from "sonner";

const STATUSES = ["New", "Confirmed", "Packed", "Shipped", "Out for Delivery", "Delivered", "Cancelled", "Returned"];
const PER_PAGE = 8;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const load = () => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);
  useEffect(() => setPage(1), [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      load();
    } catch { toast.error("Could not update status"); }
  };

  const generateInvoice = async (orderId, orderNumber) => {
    try {
      await api.post(`/admin/orders/${orderId}/generate-invoice`);
      toast.success("Invoice generated successfully");
      load();
    } catch (e) {
      toast.error("Could not generate invoice");
    }
  };

  const downloadInvoice = async (orderId, orderNumber) => {
    try {
      const response = await fetch(`${API}/admin/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("bc_token")}` }
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${orderNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download invoice");
    }
  };

  const { paged, totalPages } = usePageSlice(orders, page, PER_PAGE);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold">Orders</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-card border-border" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="admin bg-card border-border">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {orders.length === 0 ? <p className="text-muted-foreground">No orders.</p> : (
        <>
        <div className="space-y-3">
          {paged.map((o) => (
            <div key={o.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`admin-order-${o.order_number}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-mono font-semibold">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                  <p className="text-sm mt-2">{o.customer.name} · {o.customer.phone}</p>
                  <p className="text-xs text-muted-foreground max-w-md">{o.customer.address}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-lg">{rupee(o.total)}</p>
                  <p className="text-xs text-muted-foreground">{o.payment_method.toUpperCase()} · {o.payment_note}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border space-y-3">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-sm text-primary font-medium" data-testid={`order-items-${o.order_number}`}>{o.items.length} item(s) — view</button>
                    </DialogTrigger>
                    <DialogContent className="admin bg-card border-border text-foreground">
                      <DialogHeader><DialogTitle>Order {o.order_number}</DialogTitle></DialogHeader>
                      <div className="space-y-2">
                        {o.items.map((it, i) => (
                          <div key={i} className="flex justify-between text-sm border-b border-border py-2">
                            <span>{it.name} <span className="text-muted-foreground">({[it.size, it.color].filter(Boolean).join("/")}) × {it.qty}</span></span>
                            <span className="font-mono">{rupee(it.price * it.qty)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">Status timeline:</div>
                      {o.status_history?.map((h, i) => (
                        <div key={i} className="text-xs flex justify-between"><span>{h.status}{h.via ? ` (${h.via})` : ""}</span><span className="text-muted-foreground">{new Date(h.at).toLocaleString("en-IN")}</span></div>
                      ))}
                    </DialogContent>
                  </Dialog>
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className="w-48 bg-secondary border-border h-9" data-testid={`order-status-select-${o.order_number}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="admin bg-card border-border">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  {!o.invoice_path ? (
                    <button
                      onClick={() => generateInvoice(o.id, o.order_number)}
                      className="inline-flex items-center gap-2 text-sm bg-secondary hover:bg-primary hover:text-primary-foreground border border-border rounded-full px-4 py-2 transition-colors"
                      data-testid={`generate-invoice-${o.order_number}`}
                    >
                      <FileText className="w-4 h-4" /> Generate Invoice
                    </button>
                  ) : (
                    <button
                      onClick={() => downloadInvoice(o.id, o.order_number)}
                      className="inline-flex items-center gap-2 text-sm bg-primary text-primary-foreground border border-primary rounded-full px-4 py-2 hover:bg-primary/90 transition-colors"
                      data-testid={`download-invoice-${o.order_number}`}
                    >
                      <Download className="w-4 h-4" /> Download Invoice
                    </button>
                  )}
                  {o.invoice_generated_at && (
                    <span className="text-xs text-muted-foreground">
                      Generated {new Date(o.invoice_generated_at).toLocaleDateString("en-IN")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}
